'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard, Package, Truck, Box, Users, Settings, Bell, Search,
  DollarSign, BarChart3, FileText, Warehouse, PackageCheck, ShoppingCart, Boxes,
  Sun, Moon, Network
} from 'lucide-react';

const Badge = ({ type, text, isDark = true }: { type: string; text: string; isDark?: boolean }) => {
  const darkStyles: Record<string, string> = {
    success: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    warning: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    danger: "bg-rose-500/10 text-rose-400 border-rose-500/20",
    neutral: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  };
  
  const lightStyles: Record<string, string> = {
    success: "bg-emerald-100 text-emerald-700 border-emerald-300",
    warning: "bg-amber-100 text-amber-700 border-amber-300",
    danger: "bg-rose-100 text-rose-700 border-rose-300",
    neutral: "bg-blue-100 text-blue-700 border-blue-300",
  };
  
  const styles = isDark ? darkStyles : lightStyles;
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors duration-300 ${styles[type] || styles.neutral}`}>
      {text}
    </span>
  );
};

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const pathname = usePathname();
  const [isDarkMode, setIsDarkMode] = useState(true);

  const navSections = [
    {
      title: 'Main',
      items: [
        { id: 'overview', label: 'Overview', icon: LayoutDashboard, href: '/dashboard' },
        { id: 'clients', label: 'Clients', icon: Users, href: '/dashboard/clients' },
        { id: 'income-statement', label: 'Income Statement', icon: DollarSign, href: '/dashboard/income-statement' },
      ]
    },
    {
      title: 'Operations',
      items: [
        { id: 'orders', label: 'Orders', icon: PackageCheck, href: '/dashboard/orders' },
        { id: 'freight', label: 'Freight', icon: Truck, href: '/dashboard/freight' },
        { id: 'warehouse', label: 'Warehouse', icon: Warehouse, href: '/dashboard/warehouse' },
        { id: 'services', label: 'Services', icon: Boxes, href: '/dashboard/services' },
      ]
    },
    {
      title: 'Analytics',
      items: [
        { id: 'yoy', label: 'Year-over-Year', icon: BarChart3, href: '/dashboard/yoy' },
        { id: 'reports', label: 'Reports', icon: FileText, href: '/dashboard/reports' },
      ]
    },
    {
      title: 'System',
      items: [
        { id: 'data-flow', label: 'Data Architecture', icon: Network, href: '/dashboard/data-flow' },
        { id: 'settings', label: 'Settings', icon: Settings, href: '/dashboard/settings' },
      ]
    }
  ];

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div className={`flex h-screen font-sans selection:bg-blue-500/30 overflow-hidden transition-colors duration-300 ${
      isDarkMode 
        ? 'bg-[#0A0A0A] text-neutral-200' 
        : 'bg-gray-50 text-gray-900'
    }`}>
      
      {/* Left Sidebar */}
      <aside className={`w-64 backdrop-blur-md border-r flex flex-col transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-neutral-900/50 border-neutral-800' 
          : 'bg-white border-gray-200 shadow-sm'
      }`}>
        {/* Logo */}
        <div className={`p-6 border-b transition-colors duration-300 ${
          isDarkMode ? 'border-neutral-800' : 'border-gray-200'
        }`}>
          <Link href="/dashboard">
            <div className={`flex items-center gap-2 font-bold text-xl tracking-tight transition-colors duration-300 cursor-pointer ${
              isDarkMode ? 'text-white hover:text-blue-400' : 'text-gray-900 hover:text-blue-600'
            }`}>
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Box size={18} className="text-white" />
              </div>
              <span>Calibrate</span>
            </div>
          </Link>
          <p className={`text-xs mt-1 transition-colors duration-300 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
            Business Intelligence
          </p>
          <div className="mt-2">
            <Badge type="success" text="Real Data" isDark={isDarkMode} />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
          {navSections.map((section) => (
            <div key={section.title}>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-2 px-3 transition-colors duration-300 ${
                isDarkMode ? 'text-neutral-500' : 'text-gray-500'
              }`}>
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = pathname === item.href || (item.href !== '/dashboard' && pathname?.startsWith(item.href));
                  return (
                    <Link key={item.id} href={item.href}>
                      <div
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                          isActive
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                            : isDarkMode
                            ? 'text-neutral-400 hover:text-white hover:bg-neutral-800/50'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <Icon size={18} />
                        <span className="font-medium">{item.label}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User Profile */}
        <div className={`p-4 border-t transition-colors duration-300 ${
          isDarkMode ? 'border-neutral-800' : 'border-gray-200'
        }`}>
          <div className={`flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer transition-colors ${
            isDarkMode ? 'hover:bg-neutral-800/50' : 'hover:bg-gray-100'
          }`}>
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold">
              AD
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Admin User</p>
              <p className={`text-xs truncate transition-colors duration-300 ${
                isDarkMode ? 'text-neutral-500' : 'text-gray-500'
              }`}>admin@calibrate.com</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        
        {/* Top Header */}
        <header className={`backdrop-blur-lg border-b px-6 py-4 flex items-center justify-between transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-[#0A0A0A]/80 border-neutral-800' 
            : 'bg-white/80 border-gray-200'
        }`}>
          <div className="flex items-center gap-4">
            {/* Page title will be set by individual pages */}
          </div>

          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className={`absolute left-3 top-2.5 transition-colors duration-300 ${
                isDarkMode ? 'text-neutral-500' : 'text-gray-400'
              }`} size={16} />
              <input 
                type="text" 
                placeholder="Search..." 
                className={`border rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-blue-500 w-64 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600'
                    : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                }`}
              />
            </div>
            <button 
              onClick={toggleTheme}
              className={`p-2 transition-colors duration-300 ${
                isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button className={`p-2 relative transition-colors duration-300 ${
              isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}>
              <Bell size={20} />
            </button>
          </div>
        </header>

        {/* Scrollable Content - Children */}
        <main className={`flex-1 overflow-y-auto transition-colors duration-300 ${
          isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
        }`}>
          {children}
        </main>
      </div>
    </div>
  );
}
